import { loadStripe, Stripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import PaymentForm, {
  type PaymentResultStatus,
} from "../components/payment-form";
import PaymentSuccess from "../components/payment-success";
import InvoiceDetailsModal from "../components/invoice-details-modal";
import Header from "../components/header";
import { useInvoiceDetails } from "../data/invoice";
import { isColorLight, getContrastColor } from "../utils/colors";
import { formatCurrency, formatDate } from "../utils/format";

function InvoicePayment() {
  const { t } = useTranslation();
  const { invoiceId } = useParams<{ invoiceId: string }>();

  // TanStack Query for invoice data
  const {
    data: invoiceData,
    isLoading,
    isError,
    error,
    refetch,
  } = useInvoiceDetails(invoiceId);

  // Local state for Stripe and UI
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [isStripeLoading, setIsStripeLoading] = useState(true);
  // Tracks `loadStripe` returning null or throwing — without it the
  // page falls through to a blank-screen `return null` fallback when
  // the Stripe script is blocked by an ad-blocker / privacy extension
  // / CSP / network issue (see same fix in checkout-payment.tsx).
  const [stripeInitFailed, setStripeInitFailed] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  // Tracks whether the completed payment is fully captured (`succeeded`)
  // or still being processed asynchronously (`processing`), so the
  // confirmation screen can show an accurate state instead of claiming
  // the amount was paid when it is only pending.
  const [paymentResultStatus, setPaymentResultStatus] =
    useState<PaymentResultStatus>("succeeded");
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isHighlightsOpen, setIsHighlightsOpen] = useState(false);

  // Get brand colors with fallbacks
  const brandColor = invoiceData?.business.brandColor || "#eb5e27";
  const secondaryColor = invoiceData?.business.secondaryColor || "#FFF7F0";

  // Determine text colors based on background brightness
  const isLightBg = isColorLight(secondaryColor);
  const textColor = getContrastColor(secondaryColor);
  const textMutedColor = isLightBg
    ? "rgba(0,0,0,0.6)"
    : "rgba(255,255,255,0.7)";

  // Load Stripe and handle 3DS redirect when invoice data is available
  useEffect(() => {
    if (!invoiceData) return;

    const stripeAccountId = invoiceData.stripeAccountId;
    let cancelled = false;

    async function initStripe() {
      try {
        const stripe = await loadStripe(
          import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "",
          { stripeAccount: stripeAccountId },
        );
        if (cancelled) return;

        // Null = Stripe.js script failed to load (ad-blocker /
        // privacy extension / CSP / offline). Surface an explicit
        // error instead of falling through to the `return null`
        // blank-screen fallback.
        if (!stripe) {
          setStripeInitFailed(true);
          setIsStripeLoading(false);
          return;
        }

        setStripeInstance(stripe);

        const urlParams = new URLSearchParams(window.location.search);
        const paymentIntentClientSecret = urlParams.get(
          "payment_intent_client_secret",
        );

        if (paymentIntentClientSecret) {
          try {
            const { paymentIntent } = await stripe.retrievePaymentIntent(
              paymentIntentClientSecret,
            );
            if (cancelled) return;

            if (paymentIntent?.status === "succeeded") {
              setPaymentSuccess(true);
              window.history.replaceState({}, "", window.location.pathname);
            }
          } catch (retrieveErr) {
            console.warn("[invoice] retrievePaymentIntent failed", retrieveErr);
          }
        }
      } catch (err) {
        if (cancelled) return;
        console.error("[invoice] Stripe init failed", err);
        setStripeInitFailed(true);
      } finally {
        if (!cancelled) setIsStripeLoading(false);
      }
    }

    initStripe();

    return () => {
      cancelled = true;
    };
  }, [invoiceData]);

  const handlePaymentSuccess = (status: PaymentResultStatus) => {
    setPaymentResultStatus(status);
    setPaymentSuccess(true);
  };

  // Derive error message from query error
  const getErrorMessage = () => {
    if (!invoiceId) return t("gymkeePay.invoice.idMissing");
    if (error) {
      const axiosError = error as { response?: { status?: number; data?: { message?: string } }; code?: string };
      if (axiosError.response?.status === 404) {
        return t("gymkeePay.invoice.notFound");
      }
      if (axiosError.response?.status === 400) {
        // Invoice exists but is in a non-accessible state (draft, void, uncollectible)
        return t("gymkeePay.invoice.notAvailable");
      }
      // Network error (no response from server) — use the same generic message
      // since the retry button will handle recovery
      return t("gymkeePay.invoice.failedToLoad");
    }
    return t("gymkeePay.invoice.failedToLoad");
  };

  // Check if the error is retryable (not 404 or 400)
  const isRetryableError = () => {
    if (!error) return false;
    const axiosError = error as { response?: { status?: number } };
    const status = axiosError.response?.status;
    return status !== 404 && status !== 400;
  };

  // Loading state
  if (isLoading || (invoiceData && isStripeLoading)) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="absolute left-0 top-0 p-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 animate-pulse rounded-lg bg-gray-300"></div>
            <div className="h-5 w-32 animate-pulse rounded bg-gray-300"></div>
          </div>
        </div>

        <div className="flex min-h-screen flex-col items-center justify-center px-4 py-20">
          <div className="mb-4 w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <div className="h-9 w-36 animate-pulse rounded bg-gray-200"></div>
                <div className="mt-2 h-4 w-28 animate-pulse rounded bg-gray-200"></div>
              </div>
              <div className="h-8 w-24 animate-pulse rounded-lg bg-gray-200"></div>
            </div>
            <div className="space-y-2 border-t border-gray-100 pt-4">
              <div className="flex justify-between">
                <div className="h-4 w-8 animate-pulse rounded bg-gray-200"></div>
                <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
              </div>
              <div className="flex justify-between">
                <div className="h-4 w-12 animate-pulse rounded bg-gray-200"></div>
                <div className="h-4 w-28 animate-pulse rounded bg-gray-200"></div>
              </div>
              <div className="flex justify-between">
                <div className="h-4 w-14 animate-pulse rounded bg-gray-200"></div>
                <div className="h-4 w-20 animate-pulse rounded bg-gray-200"></div>
              </div>
            </div>
            <div className="mt-4 flex justify-center">
              <div className="h-4 w-48 animate-pulse rounded bg-gray-200"></div>
            </div>
          </div>

          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
            <div className="mb-4 h-5 w-24 animate-pulse rounded bg-gray-200"></div>
            <div className="space-y-4">
              <div className="h-11 w-full animate-pulse rounded-lg bg-gray-200"></div>
              <div className="h-11 w-full animate-pulse rounded-lg bg-gray-200"></div>
              <div className="flex gap-4">
                <div className="h-11 w-1/2 animate-pulse rounded-lg bg-gray-200"></div>
                <div className="h-11 w-1/2 animate-pulse rounded-lg bg-gray-200"></div>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <div className="h-4 w-4 animate-pulse rounded bg-gray-200"></div>
              <div className="h-4 w-48 animate-pulse rounded bg-gray-200"></div>
            </div>
            <div className="mt-4 h-12 w-full animate-pulse rounded-lg bg-gray-300"></div>
          </div>

          <div className="mt-6 flex items-center gap-4">
            <div className="h-4 w-56 animate-pulse rounded bg-gray-300"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !invoiceId) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{ backgroundColor: secondaryColor }}
      >
        <div className="mx-4 max-w-md rounded-xl bg-white p-8 text-center shadow-2xl">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900">
            {t("gymkeePay.invoice.unableToLoad")}
          </h1>
          <p className="mt-2 text-gray-600">{getErrorMessage()}</p>
          {isRetryableError() && invoiceId && (
            <button
              onClick={() => refetch()}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {t("gymkeePay.invoice.retry")}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Stripe-init failure — recoverable error state. Surfaces the
  // ad-blocker / privacy-extension / CSP / network-block case
  // instead of falling through to a blank screen.
  if (stripeInitFailed && !isStripeLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{ backgroundColor: secondaryColor }}
      >
        <div className="mx-4 max-w-md rounded-xl bg-white p-8 text-center shadow-2xl">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
            <svg
              className="h-8 w-8 text-amber-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900">
            {t("gymkeePay.checkout.stripeUnavailable.title", {
              defaultValue: "We couldn't load the payment form",
            })}
          </h1>
          <p className="mt-2 text-gray-600">
            {t("gymkeePay.checkout.stripeUnavailable.message", {
              defaultValue:
                "An ad-blocker, privacy extension, or network issue is preventing the secure payment form from loading. Please disable any blocker on this page or try a different browser, then reload.",
            })}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800"
          >
            {t("gymkeePay.invoice.retry", { defaultValue: "Reload" })}
          </button>
        </div>
      </div>
    );
  }

  // Success state (from 3DS redirect or payment completion)
  if (paymentSuccess && invoiceData) {
    return (
      <PaymentSuccess invoiceData={invoiceData} status={paymentResultStatus} />
    );
  }

  // Already paid state
  if (invoiceData?.invoice.status === "paid") {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{ backgroundColor: secondaryColor }}
      >
        <div className="mx-4 max-w-md rounded-xl bg-white p-8 text-center shadow-2xl">
          <div
            className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
            style={{ backgroundColor: `${brandColor}20` }}
          >
            <svg
              className="h-8 w-8"
              style={{ color: brandColor }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900">
            {t("gymkeePay.invoice.alreadyPaid")}
          </h1>
          <p className="mt-2 text-gray-600">
            {t("gymkeePay.invoice.alreadyPaidMessage")}
          </p>
          {invoiceData.invoice.number && (
            <p className="mt-4 text-sm text-gray-500">
              {t("gymkeePay.paymentSuccess.invoice")}{" "}
              {invoiceData.invoice.number}
            </p>
          )}
          {invoiceData.invoice.invoicePdf && (
            <a
              href={invoiceData.invoice.invoicePdf}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-block rounded-lg px-6 py-3 font-semibold text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: brandColor }}
            >
              {t("gymkeePay.invoice.downloadReceipt", {
                defaultValue: "Download my receipt",
              })}
            </a>
          )}
        </div>
      </div>
    );
  }

  // Invoice not available (void, uncollectible, etc.)
  if (invoiceData && invoiceData.invoice.status !== "open") {
    const errorMessage =
      invoiceData.invoice.status === "void"
        ? t("gymkeePay.invoice.voided")
        : invoiceData.invoice.status === "uncollectible"
          ? t("gymkeePay.invoice.uncollectible")
          : t("gymkeePay.invoice.notAvailable");

    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{ backgroundColor: secondaryColor }}
      >
        <div className="mx-4 max-w-md rounded-xl bg-white p-8 text-center shadow-2xl">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900">
            {t("gymkeePay.invoice.unableToLoad")}
          </h1>
          <p className="mt-2 text-gray-600">{errorMessage}</p>
        </div>
      </div>
    );
  }

  // Ready state - show payment form
  if (invoiceData && stripeInstance) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: secondaryColor }}>
        <div className="absolute left-0 right-0 top-0">
          <Header
            logo={invoiceData.business.logo}
            businessName={invoiceData.business.name}
            textColor={textColor}
          />
        </div>

        <div className="flex min-h-screen flex-col items-center justify-center px-4 py-20">
          <div className="mb-4 w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(
                    invoiceData.invoice.amountDue,
                    invoiceData.invoice.currency,
                  )}
                </p>
                {invoiceData.invoice.dueDate && (
                  <p className="mt-1 text-sm text-gray-500">
                    {t("gymkeePay.invoice.due", {
                      date: formatDate(invoiceData.invoice.dueDate),
                    })}
                  </p>
                )}
              </div>
              {invoiceData.invoice.invoicePdf && (
                <a
                  href={invoiceData.invoice.invoicePdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  {t("gymkeePay.invoice.invoicePdf")}
                </a>
              )}
            </div>

            <div className="space-y-2 border-t border-gray-100 pt-4 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">
                  {t("gymkeePay.invoice.to")}
                </span>
                <span className="font-medium text-gray-900">
                  {invoiceData.customer.name ||
                    invoiceData.customer.email ||
                    "-"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">
                  {t("gymkeePay.invoice.from")}
                </span>
                <span className="font-medium text-gray-900">
                  {invoiceData.business.name || "-"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">
                  {t("gymkeePay.paymentSuccess.invoice")}
                </span>
                <span className="font-medium text-gray-900">
                  {invoiceData.invoice.number || "-"}
                </span>
              </div>
            </div>

            <button
              className="mt-4 flex w-full cursor-pointer items-center justify-center gap-1 text-sm hover:underline"
              style={{ color: brandColor }}
              onClick={() => setIsDetailsModalOpen(true)}
            >
              {t("gymkeePay.invoice.viewDetails")}
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {invoiceData.product?.highlights?.included &&
            invoiceData.product.highlights.included.length > 0 && (
              <div className="mb-4 w-full max-w-md">
                <button
                  onClick={() => setIsHighlightsOpen(!isHighlightsOpen)}
                  className="flex w-full cursor-pointer items-center justify-between rounded-xl bg-white px-4 py-3 text-left shadow-lg transition-all hover:shadow-xl"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-8 w-8 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${brandColor}15` }}
                    >
                      <svg
                        className="h-4 w-4"
                        style={{ color: brandColor }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900">
                      {t("gymkeePay.invoice.whatYouGet")}
                    </span>
                  </div>
                  <svg
                    className={`h-5 w-5 text-gray-400 transition-transform ${isHighlightsOpen ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {isHighlightsOpen && (
                  <div className="mt-2 rounded-xl bg-white p-4 shadow-lg">
                    <ul className="space-y-2">
                      {invoiceData.product.highlights.included.map(
                        (item, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <svg
                              className="mt-0.5 h-4 w-4 shrink-0"
                              style={{ color: brandColor }}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <span className="text-sm text-gray-700">
                              {item.text}
                            </span>
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}

          {invoiceData.subscription && (
            <div className="mb-4 w-full max-w-md rounded-xl border border-blue-200 bg-blue-50 p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <svg
                  className="mt-0.5 h-5 w-5 shrink-0 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    {t("gymkeePay.invoice.recurringSubscription")}
                  </p>
                  <p className="mt-0.5 text-sm text-blue-700">
                    {t("gymkeePay.invoice.recurringSubscriptionMessage", {
                      frequency:
                        invoiceData.subscription.intervalCount > 1
                          ? `${invoiceData.subscription.intervalCount} ${t(`gymkeePay.interval.${invoiceData.subscription.interval}`)}`
                          : t(
                              `gymkeePay.frequency.${invoiceData.subscription.interval === "month" ? "monthly" : invoiceData.subscription.interval === "year" ? "yearly" : invoiceData.subscription.interval === "week" ? "weekly" : "daily"}`,
                            ),
                      productName: invoiceData.subscription.productName,
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">
              {t("gymkeePay.invoice.payWithCard")}
            </h2>
            <PaymentForm
              invoiceData={invoiceData}
              stripeInstance={stripeInstance}
              onSuccess={handlePaymentSuccess}
            />
          </div>

          <div className="mt-6 text-sm" style={{ color: textMutedColor }}>
            <span>
              {t("gymkeePay.footer.poweredBy")}{" "}
              <span className="font-semibold" style={{ color: textColor }}>
                Gymkee
              </span>
              , {t("gymkeePay.footer.securedBy")}{" "}
              <span className="font-semibold" style={{ color: textColor }}>
                Stripe
              </span>
            </span>
          </div>
        </div>

        <InvoiceDetailsModal
          invoiceData={invoiceData}
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          brandColor={brandColor}
        />
      </div>
    );
  }

  return null;
}

export default InvoicePayment;
